import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Users, FileText, Search as SearchIcon } from 'lucide-react';
import { searchPosts, searchUsers, UserSearchItem } from '../../api/search/search.api';
import { IPostModel } from '../../models';
import Loading from '../../components/loading/loading.component';
import PreviewPostDialog from '../../components/preview-post-dialog/preview-post-dialog.component';

const SearchPage = () => {
  const [params] = useSearchParams();
  const keyword = (params.get('q') || '').trim();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserSearchItem[]>([]);
  const [posts, setPosts] = useState<IPostModel[]>([]);
  const [previewPost, setPreviewPost] = useState<IPostModel | null>(null);
  const [draft, setDraft] = useState(keyword);

  useEffect(() => setDraft(keyword), [keyword]);

  useEffect(() => {
    if (!keyword) {
      setUsers([]);
      setPosts([]);
      return;
    }
    let alive = true;
    setLoading(true);
    Promise.all([
      searchUsers(keyword, 1, 10).catch(() => []),
      searchPosts(keyword, 1, 20)
        .then((r) => r?.data || [])
        .catch(() => []),
    ])
      .then(([u, p]) => {
        if (!alive) return;
        setUsers(u);
        setPosts(p);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [keyword]);

  const isEmpty = !loading && users.length === 0 && posts.length === 0;

  return (
    <div className="max-w-3xl mx-auto px-3 py-6">
      {/* Ô tìm trên đầu trang bị ẩn ở màn điện thoại nên trang tự có ô nhập riêng */}
      <form
        className="mb-4 flex items-center gap-2 md:hidden"
        onSubmit={(e) => {
          e.preventDefault();
          const q = draft.trim();
          if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
        }}
      >
        <input
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Tìm trên Cab.vn"
          aria-label="Từ khoá tìm kiếm"
          className="h-10 flex-1 rounded-full bg-secondery px-4 text-sm outline-none dark:bg-white/5 dark:text-white"
        />
        <button type="submit" aria-label="Tìm" className="rounded-full bg-[#2271B1] p-2.5 text-white">
          <SearchIcon size={18} />
        </button>
      </form>
      <h1 className="mb-1 flex items-center gap-2 text-xl font-bold text-slate-800 dark:text-white">
        <SearchIcon size={20} /> Kết quả tìm kiếm
      </h1>
      {keyword ? (
        <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
          cho từ khoá “<span className="font-semibold">{keyword}</span>”
        </p>
      ) : (
        <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
          Nhập từ khoá vào ô tìm kiếm để bắt đầu.
        </p>
      )}

      {loading && <Loading classname="mx-auto my-8" />}

      {isEmpty && keyword && (
        <div className="py-10 text-center text-slate-500 dark:text-slate-400">
          Không tìm thấy kết quả nào cho “{keyword}”.
        </div>
      )}

      {/* Người dùng (ưu tiên khớp đúng tên → nhiều follower) */}
      {!loading && users.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <Users size={16} /> Người dùng
          </h2>
          <div className="ui-card divide-y divide-[#E5E7EB] dark:divide-slate-700">
            {users.map((u) => (
              <Link
                key={u.id}
                to={`/user/${u.userName || u.id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <img
                  src={u.avatar || '/default-avatar.png'}
                  alt=""
                  className="h-11 w-11 shrink-0 rounded-full object-cover bg-slate-200 dark:bg-slate-700"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {u.fullName || u.userName || 'Người dùng'}
                  </p>
                  {u.userName && (
                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                      @{u.userName}
                    </p>
                  )}
                </div>
                <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">
                  {u.followerCount} người theo dõi
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Bài viết (khớp nội dung/tiêu đề/hashtag, xếp theo nhiều like) */}
      {!loading && posts.length > 0 && (
        <section>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <FileText size={16} /> Bài viết
          </h2>
          <div className="space-y-3">
            {posts.map((post) => {
              const img = post?.postImageResponses?.[0]?.url;
              const vid = post?.postVideoResponses?.[0]?.url;
              return (
                <button
                  key={post.id}
                  type="button"
                  onClick={() => setPreviewPost(post)}
                  className="ui-card flex w-full items-start gap-3 p-3 text-left hover:shadow-md"
                >
                  <div className="relative h-[72px] w-[72px] shrink-0 overflow-hidden rounded-lg bg-slate-100 dark:bg-slate-800">
                    {img ? (
                      <img src={img} alt="" className="h-full w-full object-cover" />
                    ) : vid ? (
                      <>
                        <video src={vid} className="h-full w-full object-cover" playsInline muted preload="metadata" />
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className="h-0 w-0 border-y-[6px] border-y-transparent border-l-[10px] border-l-white drop-shadow ml-0.5" />
                        </span>
                      </>
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-slate-400">
                        <FileText size={22} />
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {post.userFullName || post.username}
                    </p>
                    <p className="line-clamp-2 text-sm text-slate-800 dark:text-slate-100">
                      {post.title || post.content || '(không có nội dung)'}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {post.likesCount ?? 0} lượt thích
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {previewPost && (
        <PreviewPostDialog
          visible={!!previewPost}
          onclose={() => setPreviewPost(null)}
          post={previewPost}
          initialSlide={0}
        />
      )}
    </div>
  );
};

export default SearchPage;
